/**
 * useWebSocket Hook
 * React hook for WebSocket connection management and subscriptions
 * Powered by react-use-websocket for robust reconnect and heartbeat handling
 */

import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useCallback, useRef, useState, useMemo } from 'react';
import useReactWebSocket, { ReadyState } from 'react-use-websocket';

import type {
  WebSocketStatus,
  SubscriptionCallback,
  Subscription,
  WebSocketManagerOptions,
  WebSocketEvent,
} from '../types/websocket';
import { backendHealth } from '../utils/backendHealth';

export interface UseWebSocketOptions extends WebSocketManagerOptions {
  token?: string;
  autoConnect?: boolean;
}

/** agent_log can stream very frequently; invalidating ['agents'] each line refetches mounted lists and can cascade into render/effect churn. */
function shouldInvalidateAgentsQuery(messageType: string): boolean {
  return messageType.startsWith('agent') && messageType !== 'agent_log';
}

function readyStateToStatus(state: ReadyState): WebSocketStatus {
  switch (state) {
    case ReadyState.CONNECTING:
      return 'connecting';
    case ReadyState.OPEN:
      return 'connected';
    case ReadyState.CLOSING:
    case ReadyState.CLOSED:
    default:
      return 'disconnected';
  }
}

export function useWebSocket<T = unknown>(
  url: string,
  options?: UseWebSocketOptions
) {
  const queryClient = useQueryClient();

  // Token state for authenticated connections
  const [wsToken, setWsToken] = useState<string | undefined>(options?.token);
  // Whether we should attempt connection
  const [shouldConnect, setShouldConnect] = useState<boolean>(
    options?.autoConnect ?? false
  );
  const [error, setError] = useState<Error | null>(null);

  // Per-instance subscriber map: topic -> { id -> callback }
  const subsRef = useRef<
    Map<string, Map<string, SubscriptionCallback<unknown>>>
  >(new Map());
  const subCounterRef = useRef(0);

  // Auth transport (audit A5): the token is sent via the WebSocket
  // subprotocol list — ['bearer', 'bearer.<jwt>'] — instead of the legacy
  // `?token=` query param (which leaks the JWT into server logs/history).
  // The server echoes the base 'bearer' protocol back on upgrade.
  // Fallback: if the FIRST connection attempt errors before ever opening
  // (e.g. an old backend that rejects unknown subprotocols), we retry once
  // with the legacy `?token=` URL so old backends keep working.
  const [useLegacyQueryAuth, setUseLegacyQueryAuth] = useState(false);
  const hasEverOpenedRef = useRef(false);

  // Build WS URL — token rides the subprotocol unless legacy fallback is active
  const wsUrl = useMemo(() => {
    if (!shouldConnect) return null;
    if (wsToken && useLegacyQueryAuth) {
      // DEPRECATED legacy path, kept only for old backends
      return `${url}?token=${encodeURIComponent(wsToken)}`;
    }
    return url; // Token (if any) goes via Sec-WebSocket-Protocol
  }, [shouldConnect, wsToken, url, useLegacyQueryAuth]);

  // Subprotocols: ['bearer', 'bearer.<jwt>'] when authenticating via
  // subprotocol; undefined when no token (behavior identical to before) or
  // when the legacy query fallback is active.
  const wsProtocols = useMemo(() => {
    if (!wsToken || useLegacyQueryAuth) return undefined;
    return ['bearer', `bearer.${wsToken}`];
  }, [wsToken, useLegacyQueryAuth]);

  const { lastJsonMessage, readyState, sendMessage } = useReactWebSocket(
    wsUrl,
    {
      protocols: wsProtocols,
      // Only reconnect while a valid URL (i.e., token) exists.
      // Prevents infinite retry loops after token expiry.
      // Also bail out when the shared backend-health monitor has flagged the
      // backend down — react-use-websocket's own reconnect loop would
      // otherwise hammer a dead server.
      shouldReconnect: () => wsUrl !== null && !backendHealth.getState().isDown,
      reconnectAttempts: options?.reconnectAttempts ?? 10,
      reconnectInterval: (attempt: number) =>
        Math.min(
          (options?.reconnectDelay ?? 1000) * Math.pow(2, attempt),
          60000
        ),
      onError: () => {
        setError(new Error('WebSocket connection error'));
        // Alternate auth styles until something opens. A permanent latch on
        // the first error would downgrade a modern backend to the
        // deprecated ?token= URL forever just because it was restarting
        // when the page loaded — alternating converges on whichever style
        // the backend actually accepts, and stops mattering after the
        // first successful open.
        if (wsToken && !hasEverOpenedRef.current) {
          setUseLegacyQueryAuth(prev => !prev);
        }
      },
      onOpen: () => {
        hasEverOpenedRef.current = true;
        setError(null);
      },
    },
    wsUrl !== null
  );

  // Dispatch incoming JSON messages to topic subscribers
  useEffect(() => {
    if (!lastJsonMessage) return;
    const msg = lastJsonMessage as any;

    // Handle ping/pong
    if (msg.type === 'ping') {
      sendMessage(JSON.stringify({ type: 'pong' }));
      return;
    }
    if (msg.type === 'pong') return;

    // Determine type and data
    let messageType = msg.type as string | undefined;
    let messageData: any = msg.data ?? msg;

    if (!messageType) {
      // Infer type from structure for flat log messages
      if ('level' in msg && 'message' in msg) {
        messageType = 'agent_log';
        messageData = msg;
      } else {
        return;
      }
    }

    // Deliver to exact-topic subscribers
    const topicSubs = subsRef.current.get(messageType);
    if (topicSubs) {
      topicSubs.forEach(cb => {
        try {
          cb(messageData);
        } catch (e) {
          console.error(
            `[useWebSocket] Error in "${messageType}" subscriber:`,
            e
          );
        }
      });
    }

    // Wildcard subscribers
    const wildcardSubs = subsRef.current.get('*');
    if (wildcardSubs) {
      wildcardSubs.forEach(cb => {
        try {
          cb(messageData);
        } catch (e) {
          console.error('[useWebSocket] Error in wildcard subscriber:', e);
        }
      });
    }

    // Prefix subscribers (e.g. "agent*")
    subsRef.current.forEach((subs, topic) => {
      if (topic !== '*' && topic.endsWith('*')) {
        const prefix = topic.slice(0, -1);
        if (messageType!.startsWith(prefix)) {
          subs.forEach(cb => {
            try {
              cb(messageData);
            } catch (e) {
              console.error(
                `[useWebSocket] Error in prefix subscriber "${topic}":`,
                e
              );
            }
          });
        }
      }
    });

    // Invalidate React Query caches
    if (shouldInvalidateAgentsQuery(messageType)) {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
    } else if (messageType.startsWith('metrics')) {
      queryClient.invalidateQueries({ queryKey: ['metrics'] });
    } else if (messageType.startsWith('camera')) {
      queryClient.invalidateQueries({ queryKey: ['cameras'] });
    } else if (messageType.startsWith('log')) {
      queryClient.invalidateQueries({ queryKey: ['logs'] });
    } else if (messageType === 'alarm_event') {
      queryClient.invalidateQueries({ queryKey: ['alarms'] });
    }
  }, [lastJsonMessage, queryClient, sendMessage]);

  const status: WebSocketStatus = readyStateToStatus(readyState);
  const isConnected = readyState === ReadyState.OPEN;

  const connect = useCallback(async (token: string) => {
    setWsToken(token);
    setShouldConnect(true);
    setError(null);
    // Fresh connection: try subprotocol auth first again
    setUseLegacyQueryAuth(false);
    hasEverOpenedRef.current = false;
  }, []);

  const disconnect = useCallback(() => {
    setShouldConnect(false);
    setWsToken(undefined);
    setUseLegacyQueryAuth(false);
    hasEverOpenedRef.current = false;
    subsRef.current.clear();
  }, []);

  const subscribe = useCallback(
    <S = unknown>(topic: string, callback: SubscriptionCallback<S>): string => {
      const id = `sub-${++subCounterRef.current}`;
      if (!subsRef.current.has(topic)) {
        subsRef.current.set(topic, new Map());
      }
      subsRef.current
        .get(topic)!
        .set(id, callback as SubscriptionCallback<unknown>);
      return id;
    },
    []
  );

  const unsubscribe = useCallback((subscriptionId: string) => {
    subsRef.current.forEach(topicSubs => {
      topicSubs.delete(subscriptionId);
    });
  }, []);

  const send = useCallback(
    (message: string) => {
      sendMessage(message);
    },
    [sendMessage]
  );

  // Build subscriptions array for API compatibility
  const subscriptions: Subscription[] = useMemo(() => {
    const result: Subscription[] = [];
    subsRef.current.forEach((topicSubs, topic) => {
      topicSubs.forEach((callback, id) => {
        result.push({ id, topic, callback });
      });
    });
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected]); // Recompute when connected state changes

  /**
   * NOTE: This callback duplicates the cache-invalidation logic already
   * handled automatically by the useEffect above (which fires on every
   * incoming WebSocket message). It is kept here because WebSocketContext
   * surfaces it via WebSocketContextValue so that consumers can imperatively
   * trigger an invalidation for a synthetic or out-of-band event that did not
   * arrive through the WebSocket message stream. Do not remove this export
   * without first removing it from WebSocketContextValue (websocket.ts) and
   * WebSocketContext.tsx, and verifying no component calls it directly.
   */
  const updateQueryCache = useCallback(
    (event: WebSocketEvent) => {
      if (shouldInvalidateAgentsQuery(event.type)) {
        queryClient.invalidateQueries({ queryKey: ['agents'] });
      } else if (event.type.startsWith('metrics')) {
        queryClient.invalidateQueries({ queryKey: ['metrics'] });
      } else if (event.type.startsWith('log')) {
        queryClient.invalidateQueries({ queryKey: ['logs'] });
      } else if (event.type.startsWith('camera')) {
        queryClient.invalidateQueries({ queryKey: ['cameras'] });
      } else if (event.type === 'alarm_event') {
        queryClient.invalidateQueries({ queryKey: ['alarms'] });
      }
    },
    [queryClient]
  );

  // Auto-connect when token option updates
  useEffect(() => {
    if (options?.autoConnect && options.token && !wsToken) {
      setWsToken(options.token);
      setShouldConnect(true);
    }
  }, [options?.autoConnect, options?.token, wsToken]);

  // Auto-connect without token (for event streams that don't need auth)
  useEffect(() => {
    if (options?.autoConnect && !options?.token) {
      setShouldConnect(true);
    }
  }, [options?.autoConnect, options?.token]);

  // Cleanup subscriptions on unmount
  useEffect(() => {
    const subscriptions = subsRef.current;

    return () => {
      subscriptions.clear();
    };
  }, []);

  return {
    isConnected,
    status,
    error,
    subscriptions,
    connect,
    disconnect,
    subscribe,
    unsubscribe,
    send,
    updateQueryCache,
  };
}
